/**
 * Packaging OS — Material / Structure page (Phase 24).
 * POST /api/workflows/material-structure/dry-run.
 */

import { useState, type FormEvent } from "react";
import {
  BUDGET_BANDS,
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  FRAGILITY_LEVELS,
  INTAKE_OBJECTIVES,
  PRICE_BANDS,
  PRODUCT_FORMS,
  SIZE_UNITS,
  runMaterialStructureDryRun,
  type ApiResult,
  type BudgetBand,
  type DryRunChannel,
  type DryRunLanguage,
  type FragilityLevel,
  type IntakeObjective,
  type MaterialStructureRequest,
  type MaterialStructureResponse,
  type PriceBand,
  type ProductForm,
  type SizeUnit,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  subcategory: string;
  product_form: ProductForm;
  size_value: string;
  size_unit: string;
  fragility: FragilityLevel;
  temperature_sensitive: boolean;
  target_market: string;
  sales_channel: string;
  price_band: PriceBand;
  packaging_objective: IntakeObjective;
  budget_band: BudgetBand;
  sustainability_preferences: string;
  production_constraints: string;
  notes: string;
  language: DryRunLanguage;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  subcategory: "",
  product_form: "other",
  size_value: "",
  size_unit: "",
  fragility: "none",
  temperature_sensitive: false,
  target_market: "",
  sales_channel: "",
  price_band: "unknown",
  packaging_objective: "new_packaging",
  budget_band: "unknown",
  sustainability_preferences: "",
  production_constraints: "",
  notes: "",
  language: "en",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<MaterialStructureResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  target_market?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

export function MaterialStructurePage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.product_name.trim()) e.product_name = t.workflowDryRunRequiredHint;
    if (!form.category.trim()) e.category = t.workflowDryRunRequiredHint;
    if (!form.target_market.trim())
      e.target_market = t.workflowDryRunRequiredHint;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const sizeNum = form.size_value.trim() ? Number(form.size_value) : null;
    const body: MaterialStructureRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      subcategory: form.subcategory.trim() || null,
      product_form: form.product_form,
      size_value: sizeNum !== null && Number.isFinite(sizeNum) ? sizeNum : null,
      size_unit: form.size_unit ? (form.size_unit as SizeUnit) : null,
      fragility: form.fragility,
      temperature_sensitive: form.temperature_sensitive,
      target_market: form.target_market.trim().toUpperCase(),
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      price_band: form.price_band,
      packaging_objective: form.packaging_objective,
      sustainability_preferences: splitLines(form.sustainability_preferences, 8),
      budget_band: form.budget_band,
      production_constraints: form.production_constraints.trim() || null,
      notes: form.notes.trim() || null,
      language: form.language,
      ai_assist: aiAssist,
    };

    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("material-structure", body, runMaterialStructureDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.materialTitle}</h1>
        <p className="po-note">{t.materialSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.intakeFieldProductSubcategory}
              value={form.subcategory}
              onChange={(v) => update("subcategory", v)}
              maxLength={100}
            />
            <SelectField
              label={t.intakeFieldProductForm}
              value={form.product_form}
              onChange={(v) => update("product_form", v as ProductForm)}
              options={opt(PRODUCT_FORMS)}
            />
            <TextField
              label={t.intakeFieldSizeValue}
              value={form.size_value}
              onChange={(v) => update("size_value", v)}
            />
            <SelectField
              label={t.intakeFieldSizeUnit}
              value={form.size_unit}
              onChange={(v) => update("size_unit", v)}
              options={opt(SIZE_UNITS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.materialFieldFragility}
              value={form.fragility}
              onChange={(v) => update("fragility", v as FragilityLevel)}
              options={opt(FRAGILITY_LEVELS)}
            />
            <SelectField
              label={t.materialFieldTemperatureSensitive}
              value={form.temperature_sensitive ? "yes" : "no"}
              onChange={(v) =>
                update("temperature_sensitive", v === "yes")
              }
              options={[
                { value: "no", label: t.reportReviewRequiredNo },
                { value: "yes", label: t.reportReviewRequiredYes },
              ]}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.intakeFieldPriceBand}
              value={form.price_band}
              onChange={(v) => update("price_band", v as PriceBand)}
              options={opt(PRICE_BANDS)}
            />
            <SelectField
              label={t.labelCopyFieldPackagingObjective}
              value={form.packaging_objective}
              onChange={(v) =>
                update("packaging_objective", v as IntakeObjective)
              }
              options={opt(INTAKE_OBJECTIVES)}
            />
            <SelectField
              label={t.intakeFieldBudgetBand}
              value={form.budget_band}
              onChange={(v) => update("budget_band", v as BudgetBand)}
              options={opt(BUDGET_BANDS)}
            />
            <SelectField
              label={t.intakeFieldLanguage}
              value={form.language}
              onChange={(v) => update("language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <TextAreaField
              label={t.materialFieldSustainabilityPreferences}
              hint={t.intakeFieldSustainabilityPreferencesHelp}
              value={form.sustainability_preferences}
              onChange={(v) => update("sustainability_preferences", v)}
              rows={3}
              full
            />
            <TextAreaField
              label={t.intakeFieldProductionConstraints}
              value={form.production_constraints}
              onChange={(v) => update("production_constraints", v)}
              rows={2}
              maxLength={500}
              full
            />
            <TextAreaField
              label={t.intakeFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={2}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.materialSubmitting
                : t.materialSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.materialReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <MaterialReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface MaterialReportProps {
  data: MaterialStructureResponse;
  requestId: string | null;
  t: UiStrings;
}

function NextSupplierLink({ t }: { t: UiStrings }): JSX.Element {
  const [locale] = useLocale();
  const target = `/${locale}/app/workflows/supplier-rfq`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-material-next-supplier"
      >
        {t.materialNextSupplier} →
      </a>
    </p>
  );
}

function MaterialReport({
  data,
  requestId,
  t,
}: MaterialReportProps): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-material-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">
        {data.draft_only_disclaimer}
      </div>
      <ReportSection title={t.materialReportFormat}>
        <BulletList items={data.recommended_packaging_format} />
      </ReportSection>
      <ReportSection title={t.materialReportMaterial}>
        <BulletList items={data.material_recommendation} />
      </ReportSection>
      <ReportSection title={t.materialReportStructure}>
        <BulletList items={data.structure_recommendation} />
      </ReportSection>
      <ReportSection title={t.materialReportDurability}>
        <BulletList
          items={data.durability_handling_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.materialReportDelivery}>
        <BulletList
          items={data.delivery_suitability_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.materialReportEcommerce}>
        <BulletList
          items={data.ecommerce_suitability_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.materialReportShelf}>
        <BulletList
          items={data.shelf_suitability_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.materialReportProduction}>
        <BulletList items={data.production_feasibility_notes} />
      </ReportSection>
      <ReportSection title={t.materialReportCost}>
        <BulletList items={data.cost_sensitivity_notes} />
      </ReportSection>
      <ReportSection title={t.materialReportSupplierQuestions}>
        <BulletList items={data.open_questions_for_supplier} />
        <NextSupplierLink t={t} />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList items={data.human_review_required_notes} />
      </ReportSection>
      <ReportSection title={t.reportTrustNotes}>
        <dl className="po-dl">
          <dt>{t.trustNotesRegulatoryGuarantee}</dt>
          <dd>
            {data.trust_notes.regulatory_guarantee
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesFinalOutput}</dt>
          <dd>
            {data.trust_notes.final_output
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.reportClaimRiskReviewRequired}</dt>
          <dd>{t.reportReviewRequiredYes}</dd>
        </dl>
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
